import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { 
  Search, 
  Send, 
  Mic, 
  MicOff, 
  Bot, 
  User, 
  Sparkles, 
  Brain, 
  Database,
  Building,
  Euro,
  FileText,
  Globe,
  Zap,
  MessageSquare,
  Volume2,
  VolumeX,
  Settings,
  ChevronUp,
  ChevronDown,
  ShoppingCart,
  ExternalLink,
  Tag,
  Star
} from 'lucide-react';

// Voice Recognition Hook (adapted from Clara KI)
const useVoiceRecognition = () => {
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const recognitionRef = useRef(null);

  const startListening = useCallback(async () => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      throw new Error('Speech recognition not supported');
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    recognitionRef.current = new SpeechRecognition();
    
    recognitionRef.current.continuous = false;
    recognitionRef.current.interimResults = true;
    recognitionRef.current.lang = 'de-DE';

    recognitionRef.current.onstart = () => {
      setIsListening(true);
      console.log('🎤 Voice recognition started');
    };

    recognitionRef.current.onresult = (event) => {
      let finalTranscript = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        if (event.results[i].isFinal) {
          finalTranscript += event.results[i][0].transcript;
        }
      }
      if (finalTranscript) {
        setTranscript(finalTranscript);
        console.log('🎤 Voice transcript:', finalTranscript);
      }
    };

    recognitionRef.current.onend = () => {
      setIsListening(false);
      console.log('🎤 Voice recognition ended');
    };

    recognitionRef.current.onerror = (event) => {
      console.error('🎤 Voice recognition error:', event.error);
      setIsListening(false);
    };

    recognitionRef.current.start();
  }, []);

  const stopListening = useCallback(() => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
    }
  }, []);

  return {
    isListening,
    transcript,
    startListening,
    stopListening
  };
};

// KITT-Style Animated Wave Component
const AnimatedWave = ({ isActive = false }) => (
  <div className="relative w-full h-1 bg-gray-700/30 rounded-full overflow-hidden">
    <div 
      className={`absolute inset-0 rounded-full transition-all duration-500 ${
        isActive ? 'opacity-100' : 'opacity-0'
      }`}
      style={{
        background: 'linear-gradient(90deg, transparent 0%, #3b82f6 20%, #6366f1 50%, #8b5cf6 80%, transparent 100%)',
        animation: isActive ? 'wave-flow 4s ease-in-out infinite' : 'none'
      }}
    />
    <style jsx>{`
      @keyframes wave-flow {
        0% { transform: translateX(-100%); opacity: 0.3; }
        50% { opacity: 0.8; }
        100% { transform: translateX(100%); opacity: 0.3; }
      }
    `}</style>
  </div>
);

// Chat Message Component with Speech Controls

// ProductCard component for shopping results
const ProductCard = ({ product, source }) => {
  return (
    <div className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-gray-700 rounded-lg p-4 mb-3 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">
            {product.name || 'Produkt'}
          </h3>
          {product.brand && (
            <div className="flex items-center mb-2">
              <Tag className="w-4 h-4 text-gray-500 mr-1" />
              <span className="text-sm text-gray-600 dark:text-gray-400">{product.brand}</span>
            </div>
          )}
          {product.description && (
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-3 line-clamp-2">
              {product.description}
            </p>
          )}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              {product.price && (
                <span className="text-lg font-bold text-green-600 dark:text-green-400">
                  {product.price}
                </span>
              )}
              {product.unit && (
                <span className="text-sm text-gray-500">
                  {product.unit}
                </span>
              )}
            </div>
            {source && source.url && (
              <a
                href={source.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center space-x-1 text-blue-600 hover:text-blue-700 dark:text-blue-400 text-sm"
              >
                <span>Zum Anbieter</span>
                <ExternalLink className="w-4 h-4" />
              </a>
            )}
          </div>
        </div>
      </div>
      {source && source.title && (
        <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
          <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
            <span>Quelle: {source.title}</span>
            <span>Aktuell</span>
          </div>
        </div>
      )}
    </div>
  );
};

// Function to extract products from shopping response
const extractProductsFromResponse = (content, sources) => {
  const products = [];
  
  // Try to extract structured product information
  const lines = content.split('\n');
  let currentProduct = {};
  
  for (const line of lines) {
    // Look for price patterns
    const priceMatch = line.match(/(\d+[,.]?\d*)\s*€\s*(?:pro|\/)?\s*(m²|m2|Stk|L|m)/gi);
    if (priceMatch) {
      const productName = line.split(/\d+[,.]?\d*\s*€/)[0].trim();
      if (productName.length > 3) {
        products.push({
          name: productName.replace(/^[-*•]\s*/, '').trim(),
          price: priceMatch[0],
          unit: priceMatch[0].match(/(m²|m2|Stk|L|m)/i)?.[0] || '',
          description: line.trim()
        });
      }
    }
    
    // Look for product names with brands
    const brandMatch = line.match(/([A-Z][a-zA-Z]+)\s+([A-Z][a-zA-Z0-9\s]+)/);
    if (brandMatch && line.includes('€')) {
      const brand = brandMatch[1];
      const productName = brandMatch[2];
      if (!products.some(p => p.name === productName)) {
        products.push({
          name: productName,
          brand: brand,
          description: line.trim()
        });
      }
    }
  }
  
  // If no structured products found, create generic ones from sources
  if (products.length === 0 && sources && sources.length > 0) {
    sources.forEach((source, index) => {
      if (source.title && !source.title.includes('Web-Recherche')) {
        products.push({
          name: source.title,
          description: source.snippet || 'Produktinformation verfügbar',
          source: source
        });
      }
    });
  }
  
  return products.slice(0, 5); // Limit to 5 products
};

const ChatMessage = ({ message, isUser, onSpeak, onStopSpeaking, isSpeaking }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const isLongMessage = message.content && message.content.length > 800;


  const toggleSources = (messageId) => {
    setShowSources(prev => ({
      ...prev,
      [messageId]: !prev[messageId]
    }));
  };

  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"} mb-6`}>
      <div className={`flex items-end space-x-3 max-w-xs lg:max-w-2xl ${isUser ? "flex-row-reverse space-x-reverse" : ""}`}>
        {/* Avatar */}
        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
          isUser 
            ? "bg-gradient-to-r from-blue-600 to-indigo-600" 
            : "bg-gradient-to-r from-purple-600 to-pink-600"
        }`}>
          {isUser ? (
            <User className="w-4 h-4 text-white" />
          ) : (
            <Bot className="w-4 h-4 text-white" />
          )}
        </div>

        {/* Message Bubble */}
        <div className={`relative px-4 py-3 rounded-2xl shadow-lg ${
          isUser
            ? "bg-gradient-to-r from-blue-600 to-indigo-600 text-white"
            : "bg-white dark:bg-slate-900 text-gray-900 dark:text-white border border-gray-200 dark:border-gray-700"
        }`}>
          {/* Loading Animation */}
          {message.isLoading ? (
            <div className="flex items-center space-x-2">
              <div className="flex space-x-1">
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
              </div>
              <span className="text-sm text-gray-500">Clara denkt nach...</span>
            </div>
          ) : (
            <>
              {/* Message Content */}
              <div className={`prose prose-sm max-w-none ${isUser ? 'prose-invert' : 'dark:prose-invert'}`}>
                {isLongMessage && !isExpanded ? (
                  <>
                    <div dangerouslySetInnerHTML={{ 
                      __html: message.content.substring(0, 800) + '...' 
                    }} />
                    <button
                      onClick={() => setIsExpanded(true)}
                      className="text-blue-500 hover:text-blue-600 text-sm font-medium mt-2 block"
                    >
                      Mehr anzeigen
                    </button>
                  </>
                ) : (
                  <div dangerouslySetInnerHTML={{ __html: message.content }} />
                )}
              </div>

              {/* Vault Sources / artifact_reference Cards */}
              {!isUser && !message.isLoading && message.sources && message.sources.length > 0 && (
                <div className="mt-3 border-t border-gray-100 dark:border-gray-700 pt-3">
                  <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-2 flex items-center gap-1">
                    <span>📎</span> Quellen ({message.sources.length})
                  </div>
                  <div className="space-y-2">
                    {message.sources.map((src, idx) => (
                      <div
                        key={src.artifact_reference || idx}
                        className="bg-gray-50 dark:bg-slate-800 rounded-lg px-3 py-2 text-xs border border-gray-200 dark:border-gray-600 cursor-pointer hover:border-blue-400 hover:bg-blue-50 dark:hover:bg-slate-700 transition-colors"
                        onClick={() => {
                          if (src.paperless_document_id) {
                            window.open(`/documents?artifact=${encodeURIComponent(src.artifact_reference)}`, '_blank', 'noopener,noreferrer');
                          }
                        }}
                        title={src.paperless_document_id ? 'Dokument öffnen' : 'Kein Vorschau verfügbar'}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-gray-800 dark:text-gray-200 truncate">
                              {src.filename || 'Unbekanntes Dokument'}
                            </div>
                            {src.property_name && (
                              <div className="text-gray-500 dark:text-gray-400 truncate">
                                {src.property_name}
                              </div>
                            )}
                            {src.excerpt && (
                              <div className="text-gray-600 dark:text-gray-300 mt-1 italic line-clamp-2">
                                &ldquo;{src.excerpt.substring(0, 150)}&rdquo;
                              </div>
                            )}
                          </div>
                          <div className="flex flex-col items-end gap-1 flex-shrink-0">
                            {src.document_type && (
                              <span className="bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 px-1.5 py-0.5 rounded text-xs">
                                {src.document_type}
                              </span>
                            )}
                            {src.relevance_score && (
                              <span className="text-gray-400 text-xs">
                                {Math.round(src.relevance_score * 100)}%
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Speech Controls for Clara Messages */}
              {!isUser && !message.isLoading && (
                <div className="flex items-center justify-end mt-2 space-x-2">
                  <button
                    onClick={() => isSpeaking ? onStopSpeaking() : onSpeak(message.content)}
                    className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    title={isSpeaking ? "Stop speaking" : "Speak message"}
                  >
                    {isSpeaking ? (
                      <VolumeX className="w-4 h-4 text-gray-500" />
                    ) : (
                      <Volume2 className="w-4 h-4 text-gray-500" />
                    )}
                  </button>
                </div>
              )}
            </>
          )}

          {/* KITT Wave for Clara messages */}
          {!isUser && (
            <div className="mt-2">
              <AnimatedWave isActive={message.isLoading || isSpeaking} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Search Mode Selector Component
const SearchModeSelector = ({ searchMode, onModeChange, isLoading }) => {
  const modes = [
    {
      id: 'intelligent',
      title: 'Interne Suche',
      icon: Database,
      description: 'Durchsucht Ihre Dokumente und Verträge',
      color: 'from-blue-500 to-blue-600',
      examples: ['Welche Rechnungen habe ich?', 'Zeige mir alle Verträge', 'Finde Clara Dokumentation']
    },
    {
      id: 'funding',
      title: 'Fördermittel',
      icon: Euro,
      description: 'KfW, BAFA, L-Bank und Innovation',
      color: 'from-green-500 to-green-600',
      examples: ['Wärmepumpe Förderung', 'Sanierung Baden-Württemberg', 'Startup Gründung']
    },
    {
      id: 'web',
      title: 'Web-Recherche',
      icon: Globe,
      description: 'Aktuelle Informationen aus dem Internet',
      color: 'from-purple-500 to-purple-600',
      examples: ['Aktuelle Immobilienpreise', 'Neue Gesetze 2024', 'Marktentwicklung']
    },
    {
      id: 'hybrid',
      title: 'Hybrid-Suche',
      icon: Zap,
      description: 'Kombiniert alle Suchquellen',
      color: 'from-orange-500 to-orange-600',
      examples: ['Clara System + Marktdaten', 'Förderung + Dokumentation', 'Umfassende Analyse']
    },
    {
      id: 'shopping',
      title: 'Shopping',
      icon: ShoppingCart,
      description: 'Preisvergleich für Bau & Einrichtung',
      color: 'from-pink-500 to-pink-600',
      examples: ['Parkett Eiche 20€/m²', 'IKEA Küche Vergleich', 'Bosch Bohrmaschine Preise']
    }
  ];


  const toggleSources = (messageId) => {
    setShowSources(prev => ({
      ...prev,
      [messageId]: !prev[messageId]
    }));
  };

  return (
    <div className="flex flex-wrap gap-2 mb-4">
      {modes.map((mode) => {
        const Icon = mode.icon;
        const isSelected = searchMode === mode.id;
        
      
  const toggleSources = (messageId) => {
    setShowSources(prev => ({
      ...prev,
      [messageId]: !prev[messageId]
    }));
  };

  return (
          <button
            key={mode.id}
            onClick={() => !isLoading && onModeChange(mode.id)}
            disabled={isLoading}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
              isSelected
                ? `bg-gradient-to-r ${mode.color} text-white shadow-md`
                : 'bg-gray-100 hover:bg-gray-200 text-gray-700 dark:bg-slate-800 dark:hover:bg-gray-600 dark:text-gray-300'
            } ${isLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
          >
            <Icon className="w-4 h-4" />
            <span>{mode.title}</span>
          </button>
        );
      })}
    </div>
  );
};

// LLM Toggle Component
const LLMToggle = ({ selectedModel, onModelChange, isLoading }) => {

  const toggleSources = (messageId) => {
    setShowSources(prev => ({
      ...prev,
      [messageId]: !prev[messageId]
    }));
  };

  return (
    <div className="flex items-center space-x-2 mb-4">
      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">KI-Modell:</span>
      <div className="flex bg-gray-100 dark:bg-slate-900 rounded-lg p-1">
        <button
          onClick={() => !isLoading && onModelChange('mistral')}
          disabled={isLoading}
          className={`px-3 py-1 rounded-md text-sm font-medium transition-all ${
            selectedModel === 'mistral'
              ? 'bg-red-500 text-white shadow-sm'
              : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
          } ${isLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
        >
          🔴 Lokal (Qwen 32B)
        </button>
        <button
          onClick={() => !isLoading && onModelChange('perplexity')}
          disabled={isLoading}
          className={`px-3 py-1 rounded-md text-sm font-medium transition-all ${
            selectedModel === 'perplexity'
              ? 'bg-green-500 text-white shadow-sm'
              : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
          } ${isLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
        >
          🌐 Perplexity (Web)
        </button>
      </div>
    </div>
  );
};

// Main Clara AI Search Component
const ClaraAISearch = () => {
  // State Management
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [searchMode, setSearchMode] = useState('funding');
  const [selectedModel, setSelectedModel] = useState('mistral');
  const [isSpeaking, setIsSpeaking] = useState(false);
  
  // Speech Synthesis State (like Clara KI)
  const [availableVoices, setAvailableVoices] = useState([]);
  const [selectedVoice, setSelectedVoice] = useState(null);
  
  // UI State for collapsible interface
  const [isSettingsExpanded, setIsSettingsExpanded] = useState(false);
  const [showModeCards, setShowModeCards] = useState(true);
  
  // Refs
  const messagesEndRef = useRef(null);
  const inFlightRef = useRef(false);
  const speechSynthesisRef = useRef(null);
  
  // Voice Recognition
  const { isListening, transcript, startListening, stopListening } = useVoiceRecognition();

  // Auto-scroll to bottom
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Initialize Speech Output (Clara KI working version)
  useEffect(() => {
    const initSpeechOutput = () => {
      if ('speechSynthesis' in window) {
        const loadVoices = () => {
          const voices = speechSynthesis.getVoices();
          console.log('🔊 Total voices available:', voices.length);
          
          // Filter für deutsche weibliche Stimmen (same as Clara KI)
          const germanFemaleVoices = voices.filter(voice => 
            voice.lang.startsWith('de') && 
            (voice.name.toLowerCase().includes('female') || 
             voice.name.toLowerCase().includes('clara') ||
             voice.name.toLowerCase().includes('anna') ||
             voice.name.toLowerCase().includes('google') ||
             voice.name.toLowerCase().includes('deutsch'))
          );
          
          console.log('🔊 German female voices found:', germanFemaleVoices.map(v => v.name));
          
          setAvailableVoices(germanFemaleVoices);
          if (germanFemaleVoices.length > 0) {
            setSelectedVoice(germanFemaleVoices[0]);
            console.log('🔊 Selected voice:', germanFemaleVoices[0].name);
          } else {
            console.warn('🔊 No German female voices found!');
          }
        };

        loadVoices();
        speechSynthesis.onvoiceschanged = loadVoices;
      } else {
        console.error('🔊 Speech Synthesis not supported in this browser');
      }
    };

    initSpeechOutput();
  }, []);
  // Mode cards remain visible for easy mode switching
  // Removed auto-collapse to allow users to switch modes anytime

  // Auto-populate input with voice transcript
  useEffect(() => {
    if (transcript && transcript.trim()) {
      console.log('🎤 Voice transcript received:', transcript.trim());
      setInputValue(transcript.trim());
    }
  }, [transcript]);

  // Speech Synthesis Functions (Clara KI working version)
  const speakText = useCallback((text) => {
    if (!('speechSynthesis' in window) || !selectedVoice) {
      console.warn('🔊 Speech Synthesis not available or no voice selected');
      return;
    }
    
    // Stop any current speech
    speechSynthesis.cancel();
    
    // Clean text for speech output (same as Clara KI)
    const cleanText = text
      .replace(/\*\*(.*?)\*\*/g, '$1') // Remove markdown bold
      .replace(/\*(.*?)\*/g, '$1') // Remove markdown italic
      .replace(/#{1,6}\s/g, '') // Remove markdown headers
      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // Convert links to text
      .replace(/```[^`]*```/g, 'Code-Block') // Replace code blocks
      .replace(/`([^`]+)`/g, '$1') // Remove inline code formatting
      .replace(/\n+/g, '. ') // Replace line breaks with periods
      .replace(/\s+/g, ' ') // Normalize whitespace
      .trim();
    
    if (!cleanText) {
      console.warn('🔊 No text to speak');
      return;
    }
    
    const utterance = new SpeechSynthesisUtterance(cleanText);
    utterance.voice = selectedVoice;
    utterance.lang = 'de-DE';
    utterance.rate = 1.1; // 10% schneller (same as Clara KI)
    utterance.volume = 0.9;
    
    utterance.onstart = () => {
      setIsSpeaking(true);
      console.log('🔊 Speech started with voice:', selectedVoice.name);
    };
    utterance.onend = () => {
      setIsSpeaking(false);
      console.log('🔊 Speech ended');
    };
    utterance.onerror = (error) => {
      setIsSpeaking(false);
      console.error('🔊 Speech error:', error);
    };
    
    speechSynthesis.speak(utterance);
  }, [selectedVoice]);



  const stopSpeaking = useCallback(() => {
    if ('speechSynthesis' in window) {
      speechSynthesis.cancel();
      setIsSpeaking(false);
      console.log('🔊 Speech stopped');
    }
  }, []);

  // Handle Voice Input
  const handleVoiceInput = useCallback(async () => {
    if (isListening) {
      stopListening();
      return;
    }

    try {
      await startListening();
    } catch (error) {
      console.error('🎤 Voice recognition error:', error);
      // Show user-friendly error
      setMessages(prev => [...prev, {
        id: Date.now(),
        type: 'assistant',
        content: 'Entschuldigung, die Spracherkennung ist in Ihrem Browser nicht verfügbar. Bitte geben Sie Ihre Frage schriftlich ein.',
        timestamp: new Date().toISOString(),
        isLoading: false
      }]);
    }
  }, [isListening, startListening, stopListening]);

  // Send Message Function
  const handleSendMessage = useCallback(async (messageText) => {
    if (!messageText.trim() || isLoading || inFlightRef.current) {
      return;
    }

    console.log('📤 Sending message:', messageText);
    inFlightRef.current = true;
    setIsLoading(true);

    // Add user message
    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: messageText,
      timestamp: new Date().toISOString(),
      isLoading: false
    };

    // Add loading message
    const loadingMessage = {
      id: Date.now() + 1,
      type: 'assistant',
      content: '',
      timestamp: new Date().toISOString(),
      isLoading: true
    };

    setMessages(prev => [...prev, userMessage, loadingMessage]);

    try {
      let responseText = '';
      let sources = [];
      let finalSearchMode = searchMode;
      // Call Clara AI Search API (Backend) for all models
      const response = await fetch('/api/research/query', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          query: messageText,
          llm: searchMode === 'shopping' ? 'perplexity' : selectedModel,
          search_mode: searchMode === 'shopping' ? 'web' : searchMode,
          shopping_mode: searchMode === 'shopping',
          top_k: 5
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('📥 API response:', data);
      responseText = data.answer || 'Entschuldigung, ich konnte keine Antwort generieren.';
      sources = data.sources || [];
      finalSearchMode = data.search_mode || searchMode;

      // Format response with markdown support
      responseText = responseText
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.*?)\*/g, '<em>$1</em>')
        .replace(/### (.*?)\\n/g, '<h3>$1</h3>')
        .replace(/## (.*?)\\n/g, '<h2>$1</h2>')
        .replace(/# (.*?)\\n/g, '<h1>$1</h1>')
        .replace(/\\n/g, '<br>')
        .replace(/\n/g, '<br>');

      const assistantMessage = {
        id: loadingMessage.id,
        type: 'assistant',
        content: responseText,
        timestamp: new Date().toISOString(),
        isLoading: false,
        sources: sources,
        searchMode: finalSearchMode
      };

      setMessages(prev => prev.map(msg => 
        msg.id === loadingMessage.id ? assistantMessage : msg
      ));
      
      // Note: Auto-speech removed to prevent interruption errors
      // Users can manually click the speech button to hear responses
    } catch (error) {
      console.error('❌ Error sending message:', error);
      
      const errorMessage = {
        id: loadingMessage.id,
        type: 'assistant',
        content: 'Entschuldigung, es gab einen Fehler bei der Verarbeitung Ihrer Anfrage. Bitte versuchen Sie es erneut.',
        timestamp: new Date().toISOString(),
        isLoading: false
      };

      setMessages(prev => prev.map(msg => 
        msg.id === loadingMessage.id ? errorMessage : msg
      ));
    } finally {
      setIsLoading(false);
      inFlightRef.current = false;
    }
  }, [selectedModel, searchMode, isLoading, speakText]);

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    if (inputValue.trim() && !isLoading) {
      const messageToSend = inputValue.trim();
      setInputValue('');
      handleSendMessage(messageToSend);
    }
  };

  // Global keyboard shortcuts
  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      // Only handle if not typing in input field
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA") return;
      
      if (e.code === "Space" && !isLoading) {
        e.preventDefault();
        handleVoiceInput();
      } else if (e.key === "Enter" && !isLoading && inputValue.trim()) {
        e.preventDefault();
        const messageToSend = inputValue.trim();
        setInputValue("");
        handleSendMessage(messageToSend);
      }
    };
    
    document.addEventListener("keydown", handleGlobalKeyDown);
  
  const toggleSources = (messageId) => {
    setShowSources(prev => ({
      ...prev,
      [messageId]: !prev[messageId]
    }));
  };

  return () => document.removeEventListener("keydown", handleGlobalKeyDown);
  }, [handleVoiceInput, handleSendMessage, isLoading, inputValue]);

  // Welcome message
  useEffect(() => {
    if (messages.length === 0) {
      const welcomeMessage = {
        id: Date.now(),
        type: 'assistant',
        content: `<strong>Willkommen bei Clara AI Search!</strong><br><br>
        Ich bin Ihr intelligenter Assistent für:<br>
        🏛️ <strong>Fördermittel-Recherche</strong> (KfW, BAFA, L-Bank)<br>
        📁 <strong>Dokumentensuche</strong> in Ihren Dateien<br>
        🌐 <strong>Web-Recherche</strong> für aktuelle Informationen<br>
        ⚡ <strong>Hybrid-Suche</strong> kombiniert alle Quellen<br><br>
        
        <em>Tipp: Drücken Sie die Leertaste für Spracheingabe oder stellen Sie direkt eine Frage!</em>`,
        timestamp: new Date().toISOString(),
        isLoading: false
      };
      setMessages([welcomeMessage]);
    }
  }, []);


// Compact Mode Selection Component
const CompactModeSelector = () => (
  <div className="bg-white dark:bg-slate-900 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 mb-4">
    {/* Header Bar */}
    <div className="flex items-center justify-between p-3 border-b border-gray-100 dark:border-gray-600">
      <div className="flex items-center space-x-4">
        {/* Current Mode Display */}
        <div className="flex items-center space-x-2">
          {(() => {
            const currentMode = modes.find(mode => mode.id === searchMode);
            const Icon = currentMode?.icon || Database;
          
  const toggleSources = (messageId) => {
    setShowSources(prev => ({
      ...prev,
      [messageId]: !prev[messageId]
    }));
  };

  return (
              <>
                <div className={`w-3 h-3 rounded-full bg-gradient-to-r ${currentMode?.color || 'from-gray-500 to-gray-600'}`}></div>
                <Icon className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                <span className="font-medium text-gray-800 dark:text-white">{currentMode?.title}</span>
              </>
            );
          })()}
        </div>

        {/* LLM Provider Display */}
        <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
          <span>•</span>
          <span>{selectedModel === 'mistral' ? '🔴 Mistral' : '🌐 Perplexity'}</span>
        </div>
      </div>

      {/* Expand/Collapse Button */}
      <button
        onClick={() => setIsSettingsExpanded(!isSettingsExpanded)}
        className="flex items-center space-x-1 px-2 py-1 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-700 rounded transition-colors"
      >
        <Settings className="w-4 h-4" />
        {isSettingsExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>
    </div>

    {/* Expandable Settings */}
    {isSettingsExpanded && (
      <div className="p-4 space-y-4">
        {/* Search Mode Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Suchmodus</label>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
            {modes.map((mode) => {
              const Icon = mode.icon;
              const isSelected = searchMode === mode.id;
              
            
  const toggleSources = (messageId) => {
    setShowSources(prev => ({
      ...prev,
      [messageId]: !prev[messageId]
    }));
  };

  return (
                <button
                  key={mode.id}
                  onClick={() => !isLoading && setSearchMode(mode.id)}
                  disabled={isLoading}
                  className={`flex items-center space-x-2 p-2 rounded-lg border transition-all ${
                    isSelected 
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300' 
                      : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700'
                  } ${isLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                >
                  <div className={`w-2 h-2 rounded-full bg-gradient-to-r ${mode.color}`}></div>
                  <Icon className="w-4 h-4" />
                  <span className="text-sm font-medium">{mode.title}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* LLM Provider Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">KI-Modell</label>
          <div className="flex space-x-2">
            <button
              onClick={() => !isLoading && setSelectedModel('mistral')}
              disabled={isLoading}
              className={`flex-1 p-2 rounded-lg border text-sm transition-all ${
                selectedModel === 'mistral'
                  ? 'border-red-500 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300'
                  : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700'
              } ${isLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
            >
              <div className="font-medium">🔴 Mistral (Lokal)</div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">Für interne Dokumente & Fördermittel</div>
            </button>
            <button
              onClick={() => !isLoading && setSelectedModel('perplexity')}
              disabled={isLoading}
              className={`flex-1 p-2 rounded-lg border text-sm transition-all ${
                selectedModel === 'perplexity'
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                  : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700'
              } ${isLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
            >
              <div className="font-medium">🌐 Perplexity (Web)</div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">Für aktuelle Web-Recherche</div>
            </button>
          </div>
        </div>
      </div>
    )}
  </div>
);

// Define modes array for both components
const modes = [
  {
    id: 'intelligent',
    title: 'Interne Suche',
    icon: Database,
    description: 'Durchsucht Ihre Dokumente und Verträge',
    color: 'from-blue-500 to-blue-600',
    examples: ['Welche Rechnungen habe ich?', 'Zeige mir alle Verträge', 'Finde Clara Dokumentation']
  },
  {
    id: 'funding',
    title: 'Fördermittel',
    icon: Euro,
    description: 'KfW, BAFA, L-Bank und Innovation',
    color: 'from-green-500 to-green-600',
    examples: ['Wärmepumpe Förderung', 'Sanierung Baden-Württemberg', 'Startup Gründung']
  },
  {
    id: 'web',
    title: 'Web-Recherche',
    icon: Globe,
    description: 'Aktuelle Informationen aus dem Internet',
    color: 'from-purple-500 to-purple-600',
    examples: ['Aktuelle Immobilienpreise', 'Neue Gesetze 2024', 'Marktentwicklung']
  },
  {
    id: 'hybrid',
    title: 'Hybrid-Suche',
    icon: Zap,
    description: 'Kombiniert alle Suchquellen',
    color: 'from-orange-500 to-orange-600',
    examples: ['Clara System + Marktdaten', 'Förderung + Dokumentation', 'Umfassende Analyse']
  },
  {
    id: 'shopping',
    title: 'Shopping',
    icon: ShoppingCart,
    description: 'Preisvergleich für Bau & Einrichtung',
    color: 'from-pink-500 to-pink-600',
    examples: ['Parkett Eiche 20€/m²', 'IKEA Küche Vergleich', 'Bosch Bohrmaschine Preise']
  }
];


  const toggleSources = (messageId) => {
    setShowSources(prev => ({
      ...prev,
      [messageId]: !prev[messageId]
    }));
  };

  return (
    <div className="flex flex-col h-screen bg-gray-50 dark:bg-black">
      {/* Header */}
      <div className="bg-white dark:bg-slate-900 border-b border-gray-200 dark:border-gray-700 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-r from-purple-600 to-pink-600 rounded-full flex items-center justify-center">
              <MessageSquare className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900 dark:text-white">Clara AI Search</h1>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Intelligenter Conversational Agent
              </p>
            </div>
          </div>
          
          <LLMToggle 
            selectedModel={selectedModel}
            onModelChange={setSelectedModel}
            isLoading={isLoading}
          />
        </div>
      </div>

      {/* Mode Selection - Conditional Display */}
      <div className="p-4">
        {showModeCards ? (
          <SearchModeSelector
            searchMode={searchMode}
            onModeChange={(mode) => {
              setSearchMode(mode);
              setShowModeCards(false);
            }}
            isLoading={isLoading}
          />
        ) : (
          <CompactModeSelector />
        )}
      </div>

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map((message) => (
          <ChatMessage
            key={message.id}
            message={message}
            isUser={message.type === 'user'}
            onSpeak={speakText}
            onStopSpeaking={stopSpeaking}
            isSpeaking={isSpeaking}
          />
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div className="bg-white dark:bg-slate-900 border-t border-gray-200 dark:border-gray-700 p-4">
        <form onSubmit={handleSubmit} className="flex items-center space-x-3">
          <div className="flex-1 relative">
            <input
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder={`Fragen Sie Clara nach ${
                searchMode === 'funding' ? 'Fördermitteln' :
                searchMode === 'web' ? 'aktuellen Informationen' :
                searchMode === 'hybrid' ? 'allem' : searchMode === 'shopping' ? 'Produkten & Preisen' : 'Ihren Dokumenten'
              }...`}
              className="w-full px-4 py-3 pr-12 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-slate-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
              disabled={isLoading}
            />
            <button
              type="button"
              onClick={handleVoiceInput}
              disabled={isLoading}
              className={`absolute right-3 top-1/2 transform -translate-y-1/2 p-2 rounded-lg transition-all ${
                isListening
                  ? 'bg-red-500 text-white animate-pulse'
                  : 'bg-gray-100 dark:bg-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-500'
              } ${isLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
              title={isListening ? "Aufnahme stoppen" : "Spracheingabe starten (Leertaste)"}
            >
              {isListening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
            </button>
          </div>
          
          <button
            type="submit"
            disabled={!inputValue.trim() || isLoading}
            className={`px-6 py-3 rounded-xl font-medium transition-all ${
              inputValue.trim() && !isLoading
                ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-700 hover:to-indigo-700 shadow-lg'
                : 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed'
            }`}
          >
            {isLoading ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <Send className="w-5 h-5" />
            )}
          </button>
        </form>
        
        <div className="mt-2 text-xs text-gray-500 dark:text-gray-400 text-center">
          Drücken Sie <kbd className="px-1 py-0.5 bg-gray-200 dark:bg-slate-800 rounded">Leertaste</kbd> für Spracheingabe oder <kbd className="px-1 py-0.5 bg-gray-200 dark:bg-slate-800 rounded">Enter</kbd> zum Senden
        </div>
      </div>
    </div>
  );
};

export default ClaraAISearch;
